import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Form, Icon, Input, Message, Modal } from 'semantic-ui-react';

import Button from '../../../components/Button';
import { AddressService } from '../../../services/address';
import { formatAmount, maskZipCode, unmaskMoney } from '../../../services/masks';
import { UFOptions } from '../../bond-insurance-budget-form/types';
import { walletPropertyTypeOptions } from '../constants';
import {
  CreateModalContent,
  CreateModalHeader,
  CreateSectionTitle
} from '../styles';

export type CreatePropertyFormValues = {
  estateId: string;
  zipcode: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  number: string;
  propertyType: string;
  complement: string;
  registration: string;
  area: string;
  furniture: string;
  suggestedSaleValue: string;
  suggestedRentalValue: string;
};

type Props = {
  open: boolean;
  userId?: string;
  showEstateField: boolean;
  estateOptions: Array<{ key: string; text: string; value: string }>;
  initialEstateId?: string;
  isSubmitting: boolean;
  submitError: string | null;
  isBackofficeTest: boolean;
  onClose: () => void;
  onSubmit: (values: CreatePropertyFormValues) => Promise<void>;
};

const initialValues: CreatePropertyFormValues = {
  estateId: '',
  zipcode: '',
  street: '',
  neighborhood: '',
  city: '',
  state: '',
  number: '',
  propertyType: '',
  complement: '',
  registration: '',
  area: '',
  furniture: '',
  suggestedSaleValue: '',
  suggestedRentalValue: ''
};

const furnitureOptions = [
  { key: 'unfurnished', text: 'Sem mobília', value: 'unfurnished' },
  {
    key: 'semi-furnished',
    text: 'Parcialmente mobiliado',
    value: 'semi-furnished'
  },
  { key: 'furnished', text: 'Mobiliado', value: 'furnished' }
];

type FormErrors = Partial<Record<keyof CreatePropertyFormValues, string>>;
type RequiredField =
  | 'estateId'
  | 'zipcode'
  | 'street'
  | 'neighborhood'
  | 'city'
  | 'state'
  | 'number'
  | 'propertyType';

const requiredFields: RequiredField[] = [
  'zipcode',
  'street',
  'neighborhood',
  'city',
  'state',
  'number',
  'propertyType'
];

const getRequiredFieldError = (field: RequiredField, value: string) => {
  if (!value.trim()) return 'Campo obrigatório.';
  if (field === 'zipcode' && value.replace(/\D/g, '').length !== 8) {
    return 'Informe um CEP válido.';
  }
  return undefined;
};

const CreatePropertyModal: React.FC<Props> = ({
  open,
  userId,
  showEstateField,
  estateOptions,
  initialEstateId,
  isSubmitting,
  submitError,
  isBackofficeTest,
  onClose,
  onSubmit
}) => {
  const [values, setValues] = useState<CreatePropertyFormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [zipLookupError, setZipLookupError] = useState<string | null>(null);
  const lastFetchedZipRef = useRef('');

  useEffect(() => {
    if (!open) return;
    setValues({ ...initialValues, estateId: initialEstateId || '' });
    setErrors({});
    setZipLookupError(null);
    lastFetchedZipRef.current = '';
  }, [initialEstateId, open]);

  const validate = (currentValues: CreatePropertyFormValues): FormErrors => {
    const validationErrors: FormErrors = {};

    requiredFields.forEach(field => {
      const error = getRequiredFieldError(field, String(currentValues[field] || ''));
      if (error) validationErrors[field] = error;
    });

    if (showEstateField) {
      const estateError = getRequiredFieldError(
        'estateId',
        String(currentValues.estateId || '')
      );
      if (estateError) validationErrors.estateId = estateError;
    }

    const areaValue = String(currentValues.area || '').trim();
    const normalizedArea = Number(areaValue.replace(',', '.'));
    if (areaValue && (Number.isNaN(normalizedArea) || normalizedArea < 0)) {
      validationErrors.area = 'Informe um valor numérico válido.';
    }

    const currencyFields: Array<keyof CreatePropertyFormValues> = [
      'suggestedSaleValue',
      'suggestedRentalValue'
    ];

    currencyFields.forEach(field => {
      const rawValue = String(currentValues[field] || '').trim();
      if (!rawValue) return;

      const normalizedValue = unmaskMoney(rawValue);
      if (Number.isNaN(normalizedValue) || normalizedValue < 0) {
        validationErrors[field] = 'Informe um valor monetário válido.';
      }
    });

    return validationErrors;
  };

  const updateField = (field: keyof CreatePropertyFormValues, value: string) => {
    setValues(previousValues => ({ ...previousValues, [field]: value }));
    setErrors(previousErrors => ({ ...previousErrors, [field]: undefined }));
    if (field === 'zipcode') setZipLookupError(null);
  };

  const updateCurrencyField = (
    field: 'suggestedSaleValue' | 'suggestedRentalValue',
    value: string
  ) => {
    updateField(field, formatAmount(value));
  };

  const validateRequiredField = (field: RequiredField) => {
    const error = getRequiredFieldError(field, String(values[field] || ''));
    setErrors(previousErrors => ({ ...previousErrors, [field]: error }));
  };

  const lookupZipCode = async (zipcode: string) => {
    const digits = zipcode.replace(/\D/g, '');
    if (digits.length !== 8 || lastFetchedZipRef.current === digits) return;

    lastFetchedZipRef.current = digits;

    try {
      setZipLookupError(null);
      setIsZipLoading(true);
      const address = await AddressService.getAddressByZipCode(digits, userId);
      setValues(previousValues => ({
        ...previousValues,
        zipcode: maskZipCode(digits),
        street: address.street || previousValues.street,
        neighborhood: address.district || previousValues.neighborhood,
        city: address.city || previousValues.city,
        state: (address.state || previousValues.state || '').toUpperCase()
      }));
      setErrors(previousErrors => ({
        ...previousErrors,
        zipcode: undefined,
        street: address.street ? undefined : previousErrors.street,
        neighborhood: address.district ? undefined : previousErrors.neighborhood,
        city: address.city ? undefined : previousErrors.city,
        state: address.state ? undefined : previousErrors.state
      }));
    } catch (_error) {
      lastFetchedZipRef.current = '';
      setZipLookupError('CEP não encontrado. Preencha o endereço manualmente.');
    } finally {
      setIsZipLoading(false);
    }
  };

  const handleZipCodeChange = (value: string) => {
    const maskedValue = maskZipCode(value || '');
    const digits = maskedValue.replace(/\D/g, '');
    updateField('zipcode', maskedValue);

    if (digits.length < 8) lastFetchedZipRef.current = '';
    if (digits.length === 8) lookupZipCode(maskedValue);
  };

  const renderError = (field: keyof CreatePropertyFormValues) => {
    return errors[field] ? (
      <small style={{ color: '#9f3a38' }}>{errors[field]}</small>
    ) : null;
  };

  const handleSubmit = async () => {
    if (isBackofficeTest) return;

    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    try {
      await onSubmit(values);
    } catch (_error) {
      // O erro da API é exibido na própria modal pelo componente pai.
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <CreateModalHeader>
          <Icon name="building" size="small" />
          Detalhes do imóvel
        </CreateModalHeader>
      </Modal.Header>

      <CreateModalContent>
        {submitError && <Message negative content={submitError} />}
        {zipLookupError && <Message warning content={zipLookupError} />}

        <Form>
          {showEstateField && (
            <Form.Group widths={3}>
              <Form.Field required error={Boolean(errors.estateId)}>
                <label>Imobiliária</label>
                <Dropdown
                  fluid
                  search
                  selection
                  placeholder="Selecione"
                  options={estateOptions}
                  value={values.estateId}
                  noResultsMessage="Nenhuma imobiliária encontrada."
                  onChange={(_, data) =>
                    updateField('estateId', String(data.value || ''))
                  }
                  onBlur={() => validateRequiredField('estateId')}
                />
                {renderError('estateId')}
              </Form.Field>
            </Form.Group>
          )}

          <CreateSectionTitle>Endereço do imóvel</CreateSectionTitle>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors.zipcode)}>
              <label>CEP</label>
              <Input
                placeholder="Digite..."
                value={values.zipcode}
                loading={isZipLoading}
                maxLength={9}
                onChange={(_, data) => handleZipCodeChange(String(data.value || ''))}
                onBlur={() => validateRequiredField('zipcode')}
              />
              {renderError('zipcode')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.street)}>
              <label>Logradouro</label>
              <Input
                placeholder="Digite..."
                value={values.street}
                onChange={(_, data) => updateField('street', String(data.value || ''))}
                onBlur={() => validateRequiredField('street')}
              />
              {renderError('street')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.neighborhood)}>
              <label>Bairro</label>
              <Input
                placeholder="Digite..."
                value={values.neighborhood}
                onChange={(_, data) =>
                  updateField('neighborhood', String(data.value || ''))
                }
                onBlur={() => validateRequiredField('neighborhood')}
              />
              {renderError('neighborhood')}
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors.city)}>
              <label>Cidade</label>
              <Input
                placeholder="Digite..."
                value={values.city}
                onChange={(_, data) => updateField('city', String(data.value || ''))}
                onBlur={() => validateRequiredField('city')}
              />
              {renderError('city')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.state)}>
              <label>UF</label>
              <Dropdown
                fluid
                search
                selection
                placeholder="Selecione"
                options={UFOptions}
                value={values.state}
                onChange={(_, data) => updateField('state', String(data.value || ''))}
                onBlur={() => validateRequiredField('state')}
              />
              {renderError('state')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.number)}>
              <label>Número</label>
              <Input
                placeholder="Digite..."
                value={values.number}
                onChange={(_, data) => updateField('number', String(data.value || ''))}
                onBlur={() => validateRequiredField('number')}
              />
              {renderError('number')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.propertyType)}>
              <label>Tipo do imóvel</label>
              <Dropdown
                fluid
                search
                selection
                placeholder="Selecione"
                options={walletPropertyTypeOptions}
                value={values.propertyType}
                onChange={(_, data) =>
                  updateField('propertyType', String(data.value || ''))
                }
                onBlur={() => validateRequiredField('propertyType')}
              />
              {renderError('propertyType')}
            </Form.Field>
          </Form.Group>

          <Form.Group widths={3}>
            <Form.Field>
              <label>Complemento</label>
              <Input
                placeholder="Digite..."
                value={values.complement}
                onChange={(_, data) =>
                  updateField('complement', String(data.value || ''))
                }
              />
            </Form.Field>
          </Form.Group>

          <CreateSectionTitle>Dados do imóvel</CreateSectionTitle>

          <Form.Group widths="equal">
            <Form.Field>
              <label>Código/Matrícula</label>
              <Input
                placeholder="Digite..."
                value={values.registration}
                onChange={(_, data) =>
                  updateField('registration', String(data.value || ''))
                }
              />
            </Form.Field>
            <Form.Field error={Boolean(errors.area)}>
              <label>Área</label>
              <Input
                placeholder="Digite..."
                value={values.area}
                onChange={(_, data) => updateField('area', String(data.value || ''))}
              />
              {renderError('area')}
            </Form.Field>
            <Form.Field>
              <label>Mobília</label>
              <Dropdown
                fluid
                selection
                clearable
                placeholder="Selecione"
                options={furnitureOptions}
                value={values.furniture}
                onChange={(_, data) => updateField('furniture', String(data.value || ''))}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths={3}>
            <Form.Field error={Boolean(errors.suggestedSaleValue)}>
              <label>Valor de venda sugerido</label>
              <Input
                placeholder="Digite..."
                value={values.suggestedSaleValue}
                onChange={(_, data) =>
                  updateCurrencyField('suggestedSaleValue', String(data.value || ''))
                }
              />
              {renderError('suggestedSaleValue')}
            </Form.Field>
            <Form.Field error={Boolean(errors.suggestedRentalValue)}>
              <label>Aluguel sugerido</label>
              <Input
                placeholder="Digite..."
                value={values.suggestedRentalValue}
                onChange={(_, data) =>
                  updateCurrencyField('suggestedRentalValue', String(data.value || ''))
                }
              />
              {renderError('suggestedRentalValue')}
            </Form.Field>
          </Form.Group>
        </Form>
      </CreateModalContent>

      <Modal.Actions>
        <Button
          light
          type="button"
          text="Cancelar"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <Button
          color="green"
          type="button"
          text="Criar imóvel"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || isBackofficeTest}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default CreatePropertyModal;
